import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  CalendarIcon,
  RefreshCw,
  AlertTriangle,
  Activity,
  Clock,
  ChevronDown,
  ChevronRight,
  User,
  Code,
} from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'

const ApiTiming = () => {
  const [date, setDate] = useState(new Date())
  const [refreshKey, setRefreshKey] = useState(0)

  const today = format(new Date(), 'yyyy-MM-dd')
  const selectedDate = format(date, 'yyyy-MM-dd')

  // API calls
  const { data: todayData, isLoading: todayLoading } = useSWR(
    [`/api/timing/today`, refreshKey],
    { refreshInterval: 30000 },
  )

  const { data: dailyData, isLoading: dailyLoading } = useSWR([
    `/api/timing/daily?date=${selectedDate}`,
    refreshKey,
  ])

  const { data: slowData, isLoading: slowLoading } = useSWR([
    `/api/timing/slow?date=${selectedDate}`,
    refreshKey,
  ])

  const { data: statsData, isLoading: statsLoading } = useSWR([
    `/api/timing/stats`,
    refreshKey,
  ])

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">API 响应时长统计</h2>
          <p className="text-muted-foreground">监控和分析系统API性能</p>
        </div>
        <Button onClick={handleRefresh} size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          刷新
        </Button>
      </div>

      {/* Today's Overview */}
      <TodayOverview data={todayData} loading={todayLoading} />

      {/* Tabs */}
      <Tabs defaultValue="daily" className="space-y-4">
        <TabsList>
          <TabsTrigger value="daily">每日数据</TabsTrigger>
          <TabsTrigger value="slow">慢接口监控</TabsTrigger>
          <TabsTrigger value="stats">接口统计</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-4">
          <DailyTimingPanel
            date={date}
            onDateChange={setDate}
            data={dailyData}
            loading={dailyLoading}
          />
        </TabsContent>

        <TabsContent value="slow" className="space-y-4">
          <SlowApiPanel
            date={date}
            onDateChange={setDate}
            data={slowData}
            loading={slowLoading}
          />
        </TabsContent>

        <TabsContent value="stats" className="space-y-4">
          <ApiStatsPanel data={statsData} loading={statsLoading} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// Today's Overview Component
const TodayOverview = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const {
    totalRequests = 0,
    averageTime = 0,
    slowRequests = 0,
  } = data?.data || {}

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-muted-foreground">
              总请求数
            </span>
          </div>
          <div className="text-2xl font-bold">
            {totalRequests.toLocaleString()}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-muted-foreground">
              平均响应时间
            </span>
          </div>
          <div className="text-2xl font-bold">{averageTime}ms</div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-muted-foreground">
              慢请求数
            </span>
          </div>
          <div className="text-2xl font-bold text-red-600">{slowRequests}</div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-muted-foreground">
              慢请求率
            </span>
          </div>
          <div className="text-2xl font-bold">
            {totalRequests > 0
              ? ((slowRequests / totalRequests) * 100).toFixed(1)
              : 0}
            %
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Date Picker Component
const DatePicker = ({ date, onDateChange }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-[240px] justify-start text-left font-normal"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'yyyy-MM-dd') : <span>选择日期</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onDateChange}
          disabled={(date) =>
            date > new Date() || date < new Date('2024-01-01')
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

// Daily Timing Panel
const DailyTimingPanel = ({ date, onDateChange, data, loading }) => {
  if (loading) {
    return <div className="text-center py-8">加载中...</div>
  }

  const requests = data?.data?.requests || []

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">每日API调用记录</h3>
        <DatePicker date={date} onDateChange={onDateChange} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {format(date, 'yyyy-MM-dd')} - 共 {requests.length} 次调用
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {requests.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                当日无API调用记录
              </p>
            ) : (
              requests
                .slice(0, 100)
                .map((request, index) => (
                  <RequestItem key={index} request={request} />
                ))
            )}
          </div>
          {requests.length > 100 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              显示前100条记录，共{requests.length}条
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Slow API Panel
const SlowApiPanel = ({ date, onDateChange, data, loading }) => {
  if (loading) {
    return <div className="text-center py-8">加载中...</div>
  }

  const slowApis = data?.data?.slowApis || []

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">慢接口监控 ({'>'}1000ms)</h3>
        <DatePicker date={date} onDateChange={onDateChange} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span>
              {format(date, 'yyyy-MM-dd')} - 共 {slowApis.length} 个慢接口
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {slowApis.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                当日无慢接口记录
              </p>
            ) : (
              slowApis.map((request, index) => (
                <RequestItem key={index} request={request} highlight />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// API Stats Panel
const ApiStatsPanel = ({ data, loading }) => {
  if (loading) {
    return <div className="text-center py-8">加载中...</div>
  }

  const stats = data?.data?.stats || []

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">接口性能统计</h3>

      <Card>
        <CardHeader>
          <CardTitle>接口响应时间排行 (按平均响应时间降序)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {stats.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                暂无统计数据
              </p>
            ) : (
              stats.map((stat, index) => (
                <StatItem key={index} stat={stat} rank={index + 1} />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Request Item Component
const RequestItem = ({ request, highlight = false }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const getStatusColor = (responseTime) => {
    if (responseTime > 2000) return 'bg-red-100 text-red-800 border-red-200'
    if (responseTime > 1000)
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    return 'bg-green-100 text-green-800 border-green-200'
  }

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET':
        return 'bg-blue-100 text-blue-800'
      case 'POST':
        return 'bg-green-100 text-green-800'
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800'
      case 'DELETE':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const hasRequestData = () => {
    const { params, body, query } = request
    return (
      (params && Object.keys(params).length > 0) ||
      (body && Object.keys(body).length > 0) ||
      (query && Object.keys(query).length > 0)
    )
  }

  const formatJson = (obj) => {
    if (!obj || Object.keys(obj).length === 0) return null
    return JSON.stringify(obj, null, 2)
  }

  return (
    <div
      className={cn(
        'rounded border transition-all duration-200',
        highlight && 'border-red-200 bg-red-50',
        !highlight && 'border-gray-200 hover:border-gray-300',
      )}
    >
      {/* Main Request Info */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center space-x-3 flex-1">
          <div className="flex items-center space-x-2">
            {hasRequestData() && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
            )}
            <Badge className={getMethodColor(request.method)}>
              {request.method}
            </Badge>
          </div>
          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
            {request.path}
          </code>
          {request.userId && (
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span>{request.userId}</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <span className="text-xs text-muted-foreground">
            {new Date(request.timestamp).toLocaleTimeString()}
          </span>
          <Badge className={getStatusColor(request.responseTime)}>
            {request.responseTime}ms
          </Badge>
          <Badge variant="outline">{request.statusCode}</Badge>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && hasRequestData() && (
        <div className="border-t bg-gray-50 p-3 space-y-3">
          {/* Query Parameters */}
          {request.query && Object.keys(request.query).length > 0 && (
            <RequestDataSection
              title="Query 参数"
              data={request.query}
              icon="?"
              color="text-blue-600"
            />
          )}

          {/* URL Parameters */}
          {request.params && Object.keys(request.params).length > 0 && (
            <RequestDataSection
              title="URL 参数"
              data={request.params}
              icon="/"
              color="text-green-600"
            />
          )}

          {/* Request Body */}
          {request.body && Object.keys(request.body).length > 0 && (
            <RequestDataSection
              title="请求体"
              data={request.body}
              icon="{}"
              color="text-purple-600"
            />
          )}
        </div>
      )}
    </div>
  )
}

// Request Data Section Component
const RequestDataSection = ({ title, data, icon, color }) => {
  const [isJsonExpanded, setIsJsonExpanded] = useState(false)

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2)
  }

  // Check if data is simple (only primitive values) or complex (nested objects)
  const isSimpleData = () => {
    return Object.values(data).every(
      (value) => typeof value !== 'object' || value === null,
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <span className={cn('text-sm font-medium', color)}>{icon}</span>
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      </div>

      {isSimpleData() ? (
        // Simple key-value display
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(data).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center space-x-2 text-sm bg-white rounded px-2 py-1 border"
            >
              <span className="font-medium text-gray-600">{key}:</span>
              <span className="text-gray-800 truncate">
                {String(value) || 'null'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        // Complex data with JSON view
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsJsonExpanded(!isJsonExpanded)}
              className="h-7 text-xs"
            >
              <Code className="h-3 w-3 mr-1" />
              {isJsonExpanded ? '收起 JSON' : '查看 JSON'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {Object.keys(data).length} 个字段
            </span>
          </div>

          {isJsonExpanded && (
            <pre className="text-xs bg-white p-3 rounded border overflow-x-auto whitespace-pre-wrap">
              {formatJson(data)}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

// Stat Item Component
const StatItem = ({ stat, rank }) => {
  const getPerformanceColor = (avgTime) => {
    if (avgTime > 2000) return 'text-red-600'
    if (avgTime > 1000) return 'text-yellow-600'
    return 'text-green-600'
  }

  return (
    <div className="flex items-center justify-between p-3 rounded border">
      <div className="flex items-center space-x-3">
        <span className="text-sm font-medium text-muted-foreground w-8">
          #{rank}
        </span>
        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
          {stat.endpoint}
        </code>
      </div>

      <div className="flex items-center space-x-4 text-sm">
        <div className="text-center">
          <div className="text-muted-foreground">调用次数</div>
          <div className="font-medium">{stat.count}</div>
        </div>
        <div className="text-center">
          <div className="text-muted-foreground">平均</div>
          <div
            className={cn('font-medium', getPerformanceColor(stat.averageTime))}
          >
            {stat.averageTime}ms
          </div>
        </div>
        <div className="text-center">
          <div className="text-muted-foreground">最小</div>
          <div className="font-medium text-green-600">{stat.minTime}ms</div>
        </div>
        <div className="text-center">
          <div className="text-muted-foreground">最大</div>
          <div className="font-medium text-red-600">{stat.maxTime}ms</div>
        </div>
      </div>
    </div>
  )
}

export default ApiTiming
